import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Download, Save, RefreshCw, Eye, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvestorPackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = location.state?.currentUser || getAdminSession();
  
  const [packData, setPackData] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  
  useEffect(() => {
    if (!currentUser) {
      toast.error('Please login to access this page');
      navigate('/admin/waitlist');
      return;
    }
    fetchData();
  }, [currentUser, navigate]);
  
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [packResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/investor-pack`),
        axios.get(`${API}/investor-pack/stats`)
      ]);
      setPackData(packResponse.data);
      setStats(statsResponse.data);
      if (packResponse.data.sections && packResponse.data.sections.length > 0) {
        setSelectedSection(packResponse.data.sections[0]);
      }
    } catch (error) {
      toast.error('Failed to load investor pack');
      console.error('Load error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/investor-pack`, {
        version: packData.version,
        sections: packData.sections
      });
      toast.success('Investor pack saved successfully');
      fetchData(); // Refresh to get updated timestamp
    } catch (error) {
      toast.error('Failed to save investor pack');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/investor-pack/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ref-assist-investor-pack-v${packData.version}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  
  const updateSection = (sectionId, field, value) => {
    setPackData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.section_id === sectionId ? { ...s, [field]: value } : s
      )
    }));
    
    if (selectedSection?.section_id === sectionId) {
      setSelectedSection(prev => ({ ...prev, [field]: value }));
    }
  };
  
  const toggleSection = (sectionId) => {
    const section = packData.sections.find(s => s.section_id === sectionId);
    updateSection(sectionId, 'enabled', !section.enabled);
  };
  
  const moveSection = (sectionId, direction) => {
    const sections = [...packData.sections];
    const index = sections.findIndex(s => s.section_id === sectionId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    sections[index].order = index + 1;
    sections[newIndex].order = newIndex + 1;
    
    setPackData(prev => ({ ...prev, sections }));
  };
  
  const addBullet = (sectionId) => {
    const section = packData.sections.find(s => s.section_id === sectionId);
    const bullets = section.bullets || [];
    updateSection(sectionId, 'bullets', [...bullets, 'New bullet point']);
  };
  
  const updateBullet = (sectionId, bulletIndex, value) => {
    const section = packData.sections.find(s => s.section_id === sectionId);
    const bullets = [...(section.bullets || [])];
    bullets[bulletIndex] = value;
    updateSection(sectionId, 'bullets', bullets);
  };
  
  const removeBullet = (sectionId, bulletIndex) => {
    const section = packData.sections.find(s => s.section_id === sectionId);
    const bullets = section.bullets.filter((_, i) => i !== bulletIndex);
    updateSection(sectionId, 'bullets', bullets);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading investor pack...</div>
      </div>
    );
  }
  
  if (!packData) return null;
  
  const isSuperAdmin = currentUser?.role === 'superadmin';
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk']">Investor Pack</h1>
              <Badge className="bg-primary/20 text-primary border-primary/30">
                <FileText className="w-3 h-3 mr-1" />
                v{packData.version}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Last updated: {new Date(packData.last_updated).toLocaleDateString()}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={fetchData}
              variant="outline"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={handleDownload}
              disabled={isDownloading}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Download className="w-4 h-4 mr-2" />
              {isDownloading ? 'Generating...' : 'Download PDF'}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/waitlist', { state: { currentUser } })}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
        
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Card className="bg-card border-border/50 p-6">
              <div className="text-sm text-muted-foreground mb-1">Waitlist Signups</div>
              <div className="text-3xl font-bold text-foreground">{stats.waitlist_signups}</div>
            </Card>
            <Card className="bg-card border-border/50 p-6">
              <div className="text-sm text-muted-foreground mb-1">Pilot Applications</div>
              <div className="text-3xl font-bold text-foreground">{stats.pilot_applications}</div>
            </Card>
            <Card className="bg-card border-border/50 p-6">
              <div className="text-sm text-muted-foreground mb-1">Total Interest</div>
              <div className="text-3xl font-bold text-foreground">{stats.total_interest}</div>
            </Card>
          </div>
        )}
        
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Section List */}
          <Card className="bg-card border-border/50 p-6 lg:col-span-1">
            <h2 className="text-xl font-semibold mb-4 font-['Space_Grotesk']">Sections</h2>
            <div className="space-y-2">
              {packData.sections.map((section, index) => (
                <div
                  key={section.section_id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedSection?.section_id === section.section_id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedSection(section)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <Switch
                        checked={section.enabled}
                        onCheckedChange={() => toggleSection(section.section_id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className={`text-sm font-medium ${!section.enabled ? 'text-muted-foreground line-through' : ''}`}>
                        {section.title}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); moveSection(section.section_id, 'up'); }}
                        disabled={index === 0}
                        className="h-6 w-6 p-0"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); moveSection(section.section_id, 'down'); }}
                        disabled={index === packData.sections.length - 1}
                        className="h-6 w-6 p-0"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          
          {/* Section Editor */}
          <Card className="bg-card border-border/50 p-6 lg:col-span-2">
            {selectedSection ? (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-['Space_Grotesk']">{selectedSection.title}</h2>
                  <Badge className={selectedSection.enabled ? 'bg-secondary/20 text-secondary' : 'bg-muted'}>
                    {selectedSection.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="section_title">Section Title</Label>
                    <Input
                      id="section_title"
                      value={selectedSection.title}
                      onChange={(e) => updateSection(selectedSection.section_id, 'title', e.target.value)}
                      className="text-lg font-semibold"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="section_content">Content</Label>
                    <Textarea
                      id="section_content"
                      value={selectedSection.content}
                      onChange={(e) => updateSection(selectedSection.section_id, 'content', e.target.value)}
                      rows={4}
                      placeholder="Main content for this section..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Bullet Points</Label>
                      <Button
                        size="sm"
                        onClick={() => addBullet(selectedSection.section_id)}
                        variant="outline"
                      >
                        Add Bullet
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(selectedSection.bullets || []).map((bullet, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={bullet}
                            onChange={(e) => updateBullet(selectedSection.section_id, index, e.target.value)}
                            placeholder="Bullet point..."
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBullet(selectedSection.section_id, index)}
                            className="text-destructive"
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      {(!selectedSection.bullets || selectedSection.bullets.length === 0) && (
                        <p className="text-sm text-muted-foreground italic">No bullet points added</p>
                      )}
                    </div>
                  </div>
                  
                  {selectedSection.section_id === 'traction' && stats && (
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">Live Stats (Auto-included in PDF)</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>Waitlist: <strong>{stats.waitlist_signups}</strong></div>
                        <div>Pilots: <strong>{stats.pilot_applications}</strong></div>
                        <div>Total: <strong>{stats.total_interest}</strong></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                Select a section to edit
              </div>
            )}
          </Card>
        </div>
        
        {/* Version Control */}
        <Card className="bg-card border-border/50 p-6 mt-6">
          <h3 className="text-lg font-semibold mb-4 font-['Space_Grotesk']">Version Control</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="version">Version Number</Label>
              <Input
                id="version"
                value={packData.version}
                onChange={(e) => setPackData(prev => ({ ...prev, version: e.target.value }))}
                placeholder="1.0"
                className="max-w-xs"
              />
            </div>
            <div className="flex-1">
              <Label>Last Updated</Label>
              <div className="text-sm text-muted-foreground mt-2">
                {new Date(packData.last_updated).toLocaleString()}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
